// app/(app)/inventory/units/[id].tsx
import AppScreen from "@/components/default/AppScreen";
import { Button, Input } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  deleteUnit,
  fetchUnitById,
  resetSelected,
  updateUnit,
} from "@/store/slices/unitSlice";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from "react-native";

export default function EditUnitScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedUnit, loading, updating, error } = useAppSelector(
    (state) => state.units,
  );

  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [errors, setErrors] = useState({ name: "", symbol: "" });

  useEffect(() => {
    // Convert id to number for the API call
    dispatch(fetchUnitById(Number(id)));
    return () => {
      dispatch(resetSelected());
    };
  }, [id]);

  useEffect(() => {
    if (selectedUnit) {
      setName(selectedUnit.name);
      setSymbol(selectedUnit.symbol);
      setStatus(selectedUnit.status);
    }
  }, [selectedUnit]);

  useEffect(() => {
    if (error) {
      Alert.alert("Error", error);
      dispatch(clearError());
    }
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = { name: "", symbol: "" };
    if (!name.trim()) newErrors.name = "Required";
    if (!symbol.trim()) newErrors.symbol = "Required";
    setErrors(newErrors);
    return !Object.values(newErrors).some((e) => e);
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    const result = await dispatch(
      updateUnit({ id: Number(id), data: { name, symbol, status } }),
    );
    if (updateUnit.fulfilled.match(result))
      Alert.alert("Success", "Unit updated", [
        { text: "OK", onPress: () => router.back() },
      ]);
  };

  const handleDelete = () => {
    Alert.alert("Delete", "Delete this unit?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          const result = await dispatch(deleteUnit(Number(id)));
          if (deleteUnit.fulfilled.match(result)) router.back();
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    idText: { fontSize: 14, color: colors.textLight, marginBottom: spacing.lg },
    inputContainer: { marginBottom: spacing.lg },
    statusContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.lg,
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    statusLabel: { fontSize: 16, fontWeight: "500", color: colors.text },
    deleteButton: { marginTop: spacing.lg },
  });

  if (loading)
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );

  return (
    <AppScreen>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
      >
        <Text style={styles.title}>Edit Unit</Text>
        <Text style={styles.idText}>ID: {id}</Text>
        <Input
          label="Unit Name"
          value={name}
          onChangeText={setName}
          error={errors.name}
          containerStyle={styles.inputContainer}
        />
        <Input
          label="Symbol"
          value={symbol}
          onChangeText={setSymbol}
          error={errors.symbol}
          containerStyle={styles.inputContainer}
        />
        <View style={styles.statusContainer}>
          <Text style={styles.statusLabel}>Status</Text>
          <Switch
            value={status === "active"}
            onValueChange={(val) => setStatus(val ? "active" : "inactive")}
            trackColor={{ false: colors.border, true: colors.primary }}
            thumbColor={colors.background}
          />
        </View>
        <Button title="Update Unit" onPress={handleUpdate} loading={updating} />
        <Button
          title="Delete Unit"
          onPress={handleDelete}
          variant="danger"
          containerStyle={styles.deleteButton}
        />
      </ScrollView>
    </AppScreen>
  );
}
